import { useState, useEffect } from 'react'
import {
  Calendar,
  Heart,
  Activity,
  Stethoscope,
  BookOpen,
  HelpCircle,
  Syringe,
  ChevronRight,
  Building2,
  List,
  Loader2,
  X,
  Smartphone
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { eventsApi, hospitalsApi, getHospitalNameFromCache, settingsApi } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { usePageTitle } from '../../hooks/usePageTitle'

// 事件类型对应的图标和颜色配置
const eventIconConfig: Record<string, { icon: React.ElementType; bg: string; color: string }> = {
  '健康体检': { icon: Stethoscope, bg: 'bg-blue-100', color: 'text-blue-600' },
  '血压测量': { icon: Activity, bg: 'bg-red-100', color: 'text-red-500' },
  '血糖检测': { icon: Heart, bg: 'bg-pink-100', color: 'text-pink-600' },
  '健康讲座': { icon: BookOpen, bg: 'bg-orange-100', color: 'text-orange-500' },
  '疫苗接种': { icon: Syringe, bg: 'bg-purple-100', color: 'text-purple-600' },
}

const cycleLabel: Record<string, string> = {
  day: '每天',
  week: '每周',
  month: '每月',
  quarter: '每季度',
  year: '每年',
}

const cycleLimitLabel: Record<string, string> = {
  day: '每天',
  week: '每周',
  month: '每月',
  quarter: '每季度',
  year: '每年',
}

const ActivitiesPage = () => {
  usePageTitle()
  const { user: authUser } = useAuth()
  const isLoggedIn = !!authUser
  const [user, setUser] = useState<any>(null)
  const [eventTypes, setEventTypes] = useState<any[]>([])
  const [hospitals, setHospitals] = useState<any[]>([])
  const [selectedHospital, setSelectedHospital] = useState<string>('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  // 每月答题
  const [quizEnabled, setQuizEnabled] = useState(false)
  const [quizQrUrl, setQuizQrUrl] = useState('')
  const [quizAppName, setQuizAppName] = useState('')
  const [showQuizModal, setShowQuizModal] = useState(false)

  useEffect(() => {
    settingsApi.getMonthlyQuiz().then((res: any) => {
      if (res) {
        setQuizEnabled(!!res.enabled)
        setQuizQrUrl(res.qrCodeUrl || '')
        setQuizAppName(res.appName || '')
      }
    }).catch(() => { /* 静默失败 */ })
  }, [])

  // 优先从 userAccount 获取完整用户信息（含 hospitalName），与首页一致
  useEffect(() => {
    try {
      const stored = localStorage.getItem('userAccount')
      if (stored) {
        setUser(JSON.parse(stored))
      } else if (authUser) {
        setUser(authUser)
      }
    } catch {
      if (authUser) setUser(authUser)
    }
  }, [authUser])

  // 当前用户所属机构名称（优先取 userAccount 的 hospitalName，否则从缓存读）
  const myHospitalName = user?.hospitalName || getHospitalNameFromCache(user?.hospitalId) || ''

  useEffect(() => {
    window.scrollTo(0, 0)
    const promises: Promise<any>[] = [eventsApi.list({ is_active: true })]
    // 未登录时需要机构列表供选择
    if (!isLoggedIn) {
      promises.push(hospitalsApi.list())
    }
    Promise.all(promises).then(([eventsRes, hospitalsRes]) => {
      setEventTypes(eventsRes.event_types || eventsRes || [])
      if (hospitalsRes) {
        setHospitals(hospitalsRes.hospitals || [])
      }
    }).catch(() => {
      setEventTypes([])
    }).finally(() => setLoading(false))
  }, [isLoggedIn])

  // 活动过滤逻辑
  const filterHospital = isLoggedIn ? myHospitalName : selectedHospital
  const filteredEvents = filterHospital
    ? eventTypes.filter(
        (e) => e.hospital_names?.includes(filterHospital) || !e.hospital_names
      )
    : eventTypes

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pb-6">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-6">
      {/* 顶部导航 */}
      <div className="bg-gradient-to-br from-green-600 to-green-700 px-4 pt-6 pb-10 relative">
        <div className="flex items-center justify-center">
          <span className="text-white font-bold text-base">积分获取方式</span>
        </div>
        <p className="text-green-100 text-sm text-center mt-2">
          参与健康活动，获取积分奖励
        </p>
        <Link
          to="/user/points"
          className="absolute top-6 right-4 flex items-center bg-white/20 hover:bg-white/30 transition-colors rounded-full px-3 py-1.5"
        >
          <svg className="w-3.5 h-3.5 text-white mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M12 6v12M8 10h8M8 14h8"/>
          </svg>
          <span className="text-white text-xs font-medium">我的积分</span>
        </Link>
      </div>

      {/* 已登录：展示当前机构卡片 */}
      {isLoggedIn && myHospitalName && (
        <div className="px-4 -mt-5 mb-4 relative z-0">
          <div className="bg-white rounded-2xl shadow-md px-4 py-3 flex items-center">
            <Building2 className="w-4 h-4 text-green-600 mr-2" />
            <span className="text-sm text-gray-700 font-medium">{myHospitalName}</span>
          </div>
        </div>
      )}

      {/* 未登录：机构选择下拉 */}
      {!isLoggedIn && (
        <div className="px-4 -mt-6 mb-4 relative z-20">
          <div
            className="bg-white rounded-2xl shadow-md px-4 py-3 flex items-center justify-between cursor-pointer"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span className="text-sm text-gray-500">选择机构</span>
            <div className="flex items-center">
              <span className="text-sm text-gray-800 font-medium">
                {selectedHospital || '全部机构'}
              </span>
              <ChevronRight className={`w-4 h-4 text-gray-400 ml-1 transition-transform ${dropdownOpen ? 'rotate-180' : '-rotate-90'}`} />
            </div>
          </div>

          {/* 下拉选项 */}
          {dropdownOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setDropdownOpen(false)}
              />
              <div className="absolute z-50 mt-2 left-4 right-4 bg-white rounded-2xl shadow-xl overflow-hidden">
                <div
                  className={`px-4 py-3 text-center text-sm transition-colors ${
                    selectedHospital === ''
                      ? 'bg-green-50 text-green-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => {
                    setSelectedHospital('')
                    setDropdownOpen(false)
                  }}
                >
                  全部机构
                </div>
                {hospitals.map((hospital) => (
                  <div
                    key={hospital.id}
                    className={`px-4 py-3 text-center text-sm transition-colors border-t border-gray-100 ${
                      selectedHospital === hospital.name
                        ? 'bg-green-50 text-green-700 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      setSelectedHospital(hospital.name)
                      setDropdownOpen(false)
                    }}
                  >
                    {hospital.name}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* 活动列表 */}
      <div className="px-4">
        <h3 className="font-bold text-gray-700 text-sm mb-3 px-1">
          积分活动（{filteredEvents.length} 项）
        </h3>
        {filteredEvents.length > 0 ? (
          <div className="space-y-3">
            {filteredEvents.map((event) => {
              const cfg = eventIconConfig[event.name] ?? {
                icon: Calendar,
                bg: 'bg-green-100',
                color: 'text-green-600',
              }
              const IconComp = cfg.icon
              return (
                <div
                  key={event.id}
                  className="bg-white rounded-2xl shadow-md p-4"
                >
                  <div className="flex items-start">
                    <div className={`w-12 h-12 ${cfg.bg} rounded-xl flex items-center justify-center mr-3 flex-shrink-0 overflow-hidden`}>
                      {event.icon ? (
                        <img src={event.icon} alt={event.name} className="w-8 h-8 object-contain" />
                      ) : (
                        <IconComp className={`w-6 h-6 ${cfg.color}`} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-800">{event.name}</p>
                        {event.enabled ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">进行中</span>
                        ) : (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">暂停</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{event.description || ''}</p>
                      <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-1">
                        {event.cycle_limit != null && (
                          <p className="text-xs text-gray-400">
                            {cycleLimitLabel[event.cycle] || event.cycle || '每周期'}最多 {event.cycle_limit} 次
                          </p>
                        )}
                        {event.max_total_count != null && (
                          <p className="text-xs text-orange-500">
                            累计最多参与 {event.max_total_count} 次
                          </p>
                        )}
                        {/* 未登录时展示机构支持信息 */}
                        {!isLoggedIn && (
                          <div className="flex items-center text-xs text-gray-400">
                            <Building2 className="w-3 h-3 mr-1" />
                            {event.hospital_names?.join('、') || '全部机构均支持'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2 pt-1">
                      <p className="text-xl font-bold text-green-600">+{event.points}</p>
                      <p className="text-xs text-gray-400">积分/次</p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">该机构暂不支持积分活动</p>
          </div>
        )}
      </div>

      {/* 温馨提示 */}
      <div className="px-4 mt-6 mb-2">
        <div className="bg-green-50 rounded-xl p-4 border border-green-100">
          <p className="text-xs font-bold text-green-700 mb-2">温馨提示</p>
          <ul className="text-xs text-green-600 space-y-1">
            <li className="flex items-start">
              <ChevronRight className="w-3 h-3 mt-0.5 mr-1 flex-shrink-0" />
              前往合作医疗机构参与活动，由医护人员扫描您的身份码录入积分
            </li>
            <li className="flex items-start">
              <ChevronRight className="w-3 h-3 mt-0.5 mr-1 flex-shrink-0" />
              每类活动在周期内有次数限制，超出限制后将不再增加积分
            </li>
            <li className="flex items-start">
              <ChevronRight className="w-3 h-3 mt-0.5 mr-1 flex-shrink-0" />
              积分到账后可在"积分兑换"页面兑换精美礼品
            </li>
          </ul>
        </div>
      </div>

      {/* 底部版权 */}
      <div className="px-4 pb-4 text-center mt-4">
        <p className="text-xs text-gray-400">{localStorage.getItem('copyright') || '© 2026 眉山疾控'}</p>
      </div>

      {/* 每月答题悬浮按钮 */}
      {quizEnabled && quizQrUrl && (
        <button
          onClick={() => setShowQuizModal(true)}
          className="fixed left-4 z-40 group active:scale-95 transition-transform"
          style={{ bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' }}
          aria-label="每月答题"
        >
          <div className="flex flex-col items-center">
            {/* 主按钮：白底 + 琥珀紫渐变 + 书本图标 + 呼吸光晕 */}
            <div className="relative">
              {/* 外层光晕：呼吸动画 */}
              <div
                className="absolute inset-0 rounded-2xl blur-md animate-quiz-breathe"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b, #a855f7)',
                }}
              />
              {/* 主体圆角白底 */}
              <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center relative quiz-icon-breathe">
                {/* 内部彩色圆角（琥珀→紫渐变） */}
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 40%, #a855f7 100%)',
                  }}
                >
                  <BookOpen className="w-6 h-6 text-white drop-shadow-sm" strokeWidth={2.4} />
                </div>
                {/* 红点提醒：放在外层不被裁切 */}
                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-gradient-to-br from-red-500 to-pink-500 border-2 border-white shadow" />
              </div>
            </div>
            <span className="mt-1.5 px-2 py-0.5 bg-white/95 backdrop-blur-sm text-gray-700 text-[11px] font-medium rounded-md shadow-md whitespace-nowrap border border-gray-100">
              每月答题
            </span>
          </div>
        </button>
      )}

      {/* 每月答题弹窗 */}
      {showQuizModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowQuizModal(false)}
        >
          <div
            onClick={e => e.stopPropagation()}
            className="bg-white w-full max-w-md max-h-[90vh] flex flex-col shadow-[0_-8px_30px_rgba(0,0,0,0.18)] sm:shadow-2xl sm:rounded-2xl rounded-t-3xl overflow-hidden animate-slide-up sm:animate-none"
          >
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(22, 163, 74, 0.12)' }}>
                  <Smartphone className="w-5 h-5" style={{ color: 'rgb(22, 163, 74)' }} />
                </div>
                <h3 className="text-base font-bold text-gray-800">每月答题</h3>
              </div>
              <button
                onClick={() => setShowQuizModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="px-6 py-6 text-center overflow-y-auto">
              <div className="bg-gradient-to-br from-purple-50 via-pink-50 to-amber-50 rounded-2xl p-4 inline-block mx-auto">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <img
                    src={quizQrUrl}
                    alt="小程序二维码"
                    className="w-48 h-48 object-contain"
                  />
                </div>
                <p
                  className="mt-3 text-sm font-bold"
                  style={{
                    background: 'linear-gradient(135deg, #a855f7, #ec4899, #f59e0b)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {quizAppName || '小程序'}
                </p>
              </div>

              <div className="mt-5 flex items-start space-x-2 text-left bg-blue-50 border border-blue-200 rounded-xl px-3 py-2.5">
                <HelpCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  长按二维码识别，点击底部小程序名称进入小程序参与答题，答题完毕后记得填写账号信息哦
                </p>
              </div>
            </div>

            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0" style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
              <button
                onClick={() => setShowQuizModal(false)}
                className="w-full py-3 rounded-xl font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors"
              >
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ActivitiesPage
